import { Box, Card, Typography, Stack, IconButton } from '@mui/material'
import { CompareArrows as CompareIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { NewsSource, NewsSourceConfig } from '../../types'
import NewsSourceLogo from '../../components/NewsSourceLogo'

interface Props {
  source: NewsSource
  onRemove?: () => void
  onMoveToOther?: () => void
  groupLabel: string
  groupColor: string
}

/**
 * Card component for displaying a news source within a comparison group.
 * Shows the source logo and name, with options to remove or move the source to the other group.
 * Styled with the group's accent color for visual distinction.
 * Used in the comparative games' setup interface.
 */
const SourceCard: React.FC<Props> = ({ source, onRemove, onMoveToOther, groupLabel, groupColor }) => {
  const config = NewsSourceConfig[source]
  return (
    <Card
      sx={{
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        border: `2px solid ${groupColor}`,
        bgcolor: `${groupColor}15`,
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s',
        },
      }}
    >
      <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <NewsSourceLogo source={source} />
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" fontWeight="medium">
          {config.name}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.5}>
        {onMoveToOther && (
          <IconButton
            size="small"
            onClick={onMoveToOther}
            title={`Move to ${groupLabel === 'Group A' ? 'Group B' : 'Group A'}`}
          >
            <CompareIcon fontSize="small" />
          </IconButton>
        )}
        {onRemove && (
          <IconButton size="small" onClick={onRemove} title="Remove from group">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Card>
  )
}

export default SourceCard
